import * as React from "react";
import { createFileRoute, Link, useRouterState } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";

import { seo } from "@/lib/seo";
import { NAP, SHOW_PHONE } from "@/lib/business";
import { Section } from "@/components/Section";
import { Container } from "@/components/Container";
import { Aurora } from "@/components/Aurora";
import { Button } from "@/components/ui/button";
// Loaded on demand: keeps the Supabase client (~200KB parsed) out of the entry chunk.
const getSupabase = async () => (await import("@/integrations/supabase/client")).supabase;

export const Route = createFileRoute("/_site/thank-you")({
  head: () =>
    seo({
      title: "Thank You",
      description: "Your request has been received. We reply within one business day.",
      path: "/thank-you",
      noindex: true,
    }),
  validateSearch: (search: Record<string, unknown>) => ({
    f: typeof search["f"] === "string" ? (search["f"] as string) : undefined,
  }),
  component: Page,
});

const HEADLINES: Record<string, string> = {
  contact: "Message received.",
  booking_request: "Request received.",
};

const COMPANY_SIZE = ["1–4", "5–19", "20–99", "100–499", "500+"];
const DRIVER = [
  "Costs are too high",
  "The team is overloaded",
  "We are growing fast",
  "A competitor is moving on AI",
  "Board or investor pressure",
];
const TIMELINE = ["As soon as possible", "Next 1–3 months", "Next 3–6 months", "Just exploring"];

function Page() {
  const { f } = Route.useSearch();
  const leadId = useRouterState({
    select: (state) => (state.location.state as { leadId?: string } | undefined)?.leadId,
  });

  return (
    <Section tone="paper" className="relative overflow-hidden">
      <Aurora variant="light" className="opacity-70" />
      <Container variant="narrow" className="relative">
        <h1 className="font-display text-display-lg text-ink-900">
          {HEADLINES[f ?? ""] ?? "Thanks."}
        </h1>
        <p className="mt-5 text-body-lg text-ink-600">
          A consultant will reply within one business day.
          {SHOW_PHONE ? (
            <>
              {" "}
              If it's urgent, call{" "}
              <a
                href={NAP.phoneHref}
                className="text-brand-700 underline underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-700"
              >
                {NAP.phone}
              </a>
              .
            </>
          ) : null}
        </p>

        {leadId ? <Qualification leadId={leadId} /> : null}

        <div className="mt-14 border-t border-ink-200 pt-8">
          <p className="font-mono text-eyebrow uppercase font-medium text-brand-700">
            While you wait
          </p>
          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:gap-8">
            <Link
              to="/tools/ai-opportunity-scorecard"
              className="inline-flex items-center gap-2 text-body font-medium text-brand-700 underline underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-700"
            >
              Take the AI Opportunity Scorecard
              <ArrowRight size={20} strokeWidth={1.5} aria-hidden="true" />
            </Link>
            <Link
              to="/services/ai-opportunity-assessment"
              className="inline-flex items-center gap-2 text-body font-medium text-brand-700 underline underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-700"
            >
              See how the assessment works
              <ArrowRight size={20} strokeWidth={1.5} aria-hidden="true" />
            </Link>
          </div>
        </div>
      </Container>
    </Section>
  );
}

function Qualification({ leadId }: { leadId: string }) {
  const id = React.useId();
  const [companySize, setCompanySize] = React.useState("");
  const [driver, setDriver] = React.useState("");
  const [timeline, setTimeline] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [done, setDone] = React.useState(false);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaving(true);
    try {
      // Runs as the public role: the grant allows writing only these three
      // columns, and the access rule limits it to a lead under 30 minutes old
      // whose answers are still blank.
      const { error } = await (
        await getSupabase()
      )
        .from("leads")
        .update({
          company_size: companySize || null,
          driver: driver || null,
          timeline: timeline || null,
        })
        .eq("id", leadId);
      if (error) throw error;
    } catch (error) {
      console.error("Qualification update failed", error);
    } finally {
      setSaving(false);
      setDone(true);
    }
  }

  if (done) {
    return (
      <p className="mt-10 rounded-2xl border border-ink-200 bg-paper-0 p-6 text-body text-ink-600">
        Thanks — that helps us prepare before we call.
      </p>
    );
  }

  const selectClass =
    "mt-2 flex h-[52px] w-full rounded-lg border border-ink-200 bg-paper-0 px-4 text-[16px] text-ink-900 transition-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-700 focus-visible:ring-offset-2 focus-visible:ring-offset-paper-50";

  return (
    <form
      onSubmit={handleSubmit}
      className="mt-10 rounded-2xl border border-ink-200 bg-paper-0 p-7"
    >
      <p className="font-mono text-eyebrow uppercase font-medium text-brand-700">Optional</p>
      <h2 className="mt-2 font-display text-h3 text-ink-900">
        Three quick answers make the first call sharper.
      </h2>

      <div className="mt-6 grid gap-5">
        <div>
          <label htmlFor={`${id}-size`} className="text-body-sm font-medium text-ink-900">
            Roughly how many people work at your company?
          </label>
          <select
            id={`${id}-size`}
            className={selectClass}
            value={companySize}
            onChange={(e) => setCompanySize(e.target.value)}
          >
            <option value="">Select an option</option>
            {COMPANY_SIZE.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor={`${id}-driver`} className="text-body-sm font-medium text-ink-900">
            What's driving this right now?
          </label>
          <select
            id={`${id}-driver`}
            className={selectClass}
            value={driver}
            onChange={(e) => setDriver(e.target.value)}
          >
            <option value="">Select an option</option>
            {DRIVER.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor={`${id}-timeline`} className="text-body-sm font-medium text-ink-900">
            When are you hoping to start?
          </label>
          <select
            id={`${id}-timeline`}
            className={selectClass}
            value={timeline}
            onChange={(e) => setTimeline(e.target.value)}
          >
            <option value="">Select an option</option>
            {TIMELINE.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
      </div>

      <Button type="submit" variant="secondary" className="mt-6" disabled={saving}>
        {saving ? "Saving" : "Send these answers"}
      </Button>
    </form>
  );
}
